package com.evoapps.evoshift.filedrop

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import kotlinx.browser.document
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.DragEvent
import org.w3c.dom.events.Event
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get

actual val supportsFileDrop: Boolean = true

/**
 * The browser: the app is one canvas, so DOM listeners on the document take the drag, and the drop
 * goes to the drop place under the pointer ([WebDropTargets]) or, when the screen has only one,
 * to that one wherever the file is let go (and the browser never opens the file instead).
 * Listeners added from Kotlin, no inline script (the CSP, SH-177).
 */
@Composable
internal actual fun Modifier.platformFileDrop(
    enabled: Boolean,
    onDragging: (Boolean) -> Unit,
    onDropped: (List<DropCandidate>) -> Boolean
): Modifier {
    val dragging = rememberUpdatedState(onDragging)
    val dropped = rememberUpdatedState(onDropped)
    val density = LocalDensity.current.density
    val target = remember { WebDropTarget() }
    target.onDragging = { dragging.value(it) }
    target.onDropped = { dropped.value(it) }
    target.density = density
    DisposableEffect(target, enabled) {
        if (enabled) WebDropTargets.add(target)
        onDispose {
            WebDropTargets.remove(target)
            target.setHovered(false)
        }
    }
    return onGloballyPositioned { target.bounds = it.boundsInWindow() }
}

/** One drop place on the screen: where it is (canvas pixels) and what it does. */
internal class WebDropTarget {
    var bounds: Rect = Rect.Zero
    var density: Float = 1f
    var onDragging: (Boolean) -> Unit = {}
    var onDropped: (List<DropCandidate>) -> Boolean = { false }
    private var hovered = false

    fun setHovered(value: Boolean) {
        if (hovered != value) {
            hovered = value
            onDragging(value)
        }
    }

    /** Its area in the page's CSS pixels, the canvas starting at [left], [top]. */
    fun cssArea(left: Double, top: Double): Rect = Rect(
        (left + bounds.left / density).toFloat(), (top + bounds.top / density).toFloat(),
        (left + bounds.right / density).toFloat(), (top + bounds.bottom / density).toFloat()
    )
}

/** The drop places on the screen and the document listeners serving them. */
internal object WebDropTargets {
    private val targets = mutableListOf<WebDropTarget>()
    private var listening = false

    fun add(target: WebDropTarget) {
        targets += target
        if (!listening) {
            listening = true
            document.addEventListener("dragenter", ::onDragOver)
            document.addEventListener("dragover", ::onDragOver)
            document.addEventListener("dragleave", ::onDragLeave)
            document.addEventListener("drop", ::onDrop)
        }
    }

    fun remove(target: WebDropTarget) {
        targets -= target
    }

    /** The place under the page point [x], [y]: the smallest one containing it, else the only one. */
    private fun targetAt(x: Double, y: Double): WebDropTarget? {
        if (targets.isEmpty()) return null
        val canvas = document.getElementById("ComposeTarget")?.getBoundingClientRect()
        val left = canvas?.left ?: 0.0
        val top = canvas?.top ?: 0.0
        val point = androidx.compose.ui.geometry.Offset(x.toFloat(), y.toFloat())
        val containing = targets.map { it to it.cssArea(left, top) }
            .filter { (_, area) -> area.width > 0f && area.height > 0f && area.contains(point) }
            .minByOrNull { (_, area) -> area.width * area.height }?.first
        return containing ?: targets.singleOrNull()
    }

    private fun hover(target: WebDropTarget?) = targets.forEach { it.setHovered(it === target) }

    private fun onDragOver(event: Event) {
        if (targets.isEmpty() || !event.carriesFiles()) return
        event.preventDefault()
        val drag = event as DragEvent
        val target = targetAt(drag.clientX.toDouble(), drag.clientY.toDouble())
        drag.dataTransfer?.dropEffect = if (target != null) "copy" else "none"
        hover(target)
    }

    private fun onDragLeave(event: Event) {
        if (targets.isEmpty() || !event.carriesFiles()) return
        // Left the page (no element under the pointer any more): nothing is held over a place.
        if ((event as DragEvent).relatedTarget == null) hover(null)
    }

    private fun onDrop(event: Event) {
        if (targets.isEmpty() || !event.carriesFiles()) return
        event.preventDefault()
        val drag = event as DragEvent
        val target = targetAt(drag.clientX.toDouble(), drag.clientY.toDouble())
        hover(null)
        val files = drag.dataTransfer?.files ?: return
        val candidates = (0 until files.length).mapNotNull { files[it] }.map(::candidate)
        if (target != null && candidates.isNotEmpty()) target.onDropped(candidates)
    }
}

private fun candidate(file: File) = DropCandidate(name = file.name, mimeType = file.type) { limit, done ->
    val reader = FileReader()
    reader.onload = { done(Int8Array(reader.result.unsafeCast<ArrayBuffer>()).unsafeCast<ByteArray>()) }
    reader.onerror = { done(null) }
    reader.readAsArrayBuffer(file.slice(0, limit))
}

/** A drag of files (not of text or a link). */
private fun Event.carriesFiles(): Boolean {
    val transfer = (this as? DragEvent)?.dataTransfer ?: return false
    val types = transfer.types
    for (i in 0 until types.size) if (types[i] == "Files") return true
    return false
}
